import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { PayrollMapping } from './PayrollMapping';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PayrollTabs } from '../components/PayrollTabs';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const buttonPrimary: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  borderRadius: 'var(--radius-default)',
  border: 'none',
  backgroundColor: 'var(--color-accent)',
  color: 'var(--color-surface)',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
};

const buttonDanger: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  borderRadius: 'var(--radius-default)',
  border: '1px solid var(--color-danger, #e53e3e)',
  backgroundColor: 'transparent',
  color: 'var(--color-danger, #e53e3e)',
  fontWeight: 500,
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};

const statusBadge: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 999,
  backgroundColor: 'rgba(72, 187, 120, 0.15)',
  color: '#2f855a',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

function relativeExpiry(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'expired (will auto-refresh)';
  const min = Math.round(ms / 60_000);
  if (min < 60) return `valid · refreshes in ${min} min`;
  const h = Math.round(min / 60);
  return `valid · refreshes in ${h} h`;
}

export function PayrollSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [connecting, setConnecting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<{
    tenantId: string;
    tenantName: string;
  } | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['xero-status'],
    queryFn: () => api.xeroStatus(),
  });

  // Surface ?xero=error|connected returned by /xero/callback redirect.
  // On a successful re-consent we MUST invalidate xero-employees and
  // barber-mappings too — otherwise React Query keeps serving the cached
  // 403 from the previous (insufficient-scope) token, and the
  // re-consent banner never disappears even when the new token is fine.
  useEffect(() => {
    const result = searchParams.get('xero');
    if (result === 'connected') {
      setErrorMsg(null);
      queryClient.invalidateQueries({ queryKey: ['xero-status'] });
      queryClient.invalidateQueries({ queryKey: ['xero-employees'] });
      queryClient.invalidateQueries({ queryKey: ['barber-mappings'] });
    } else if (result === 'error') {
      setErrorMsg(searchParams.get('message') || 'Connection failed');
    }
    if (result) {
      const next = new URLSearchParams(searchParams);
      next.delete('xero');
      next.delete('message');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, queryClient]);

  async function handleConnect() {
    setConnecting(true);
    setErrorMsg(null);
    try {
      const { authorizeUrl } = await api.xeroStartConnect();
      window.location.href = authorizeUrl;
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to start Xero auth');
      setConnecting(false);
    }
  }

  async function confirmDisconnect() {
    if (!disconnectTarget) return;
    const { tenantId } = disconnectTarget;
    setDisconnectTarget(null);
    try {
      await api.xeroDisconnect(tenantId);
      queryClient.invalidateQueries({ queryKey: ['xero-status'] });
      queryClient.invalidateQueries({ queryKey: ['xero-employees'] });
      queryClient.invalidateQueries({ queryKey: ['barber-mappings'] });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Disconnect failed');
    }
  }

  const tenants = data?.tenants ?? [];

  return (
    <div
      style={{
        padding: 'var(--spacing-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-4)',
        maxWidth: 960,
      }}
    >
      <div>
        <h1 style={{ margin: 0, marginBottom: 4 }}>Payroll · Settings & mapping</h1>
        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          Connect Xero and map each barber to a Xero employee. Required before you can create pay
          runs.
        </div>
      </div>

      <PayrollTabs />

      <div style={cardStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Xero connection</h2>
            <div
              style={{
                color: 'var(--color-text-muted)',
                fontSize: 'var(--font-size-sm)',
                marginTop: 4,
              }}
            >
              Slice 1 — OAuth handshake only. No payroll data is read or written until you map
              employees and approve later slices.
            </div>
          </div>
          {tenants.length > 0 && (
            <button style={buttonPrimary} onClick={handleConnect} disabled={connecting}>
              {connecting ? 'Redirecting…' : '+ Add organisation'}
            </button>
          )}
        </div>

        {errorMsg && (
          <div
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              backgroundColor: 'rgba(229, 62, 62, 0.1)',
              border: '1px solid var(--color-danger, #e53e3e)',
              borderRadius: 'var(--radius-default)',
              color: 'var(--color-danger, #e53e3e)',
              marginBottom: 'var(--spacing-3)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {errorMsg}
          </div>
        )}

        {isLoading ? (
          <div>Loading…</div>
        ) : tenants.length === 0 ? (
          <div
            style={{
              padding: 'var(--spacing-4)',
              border: '1px dashed var(--color-border)',
              borderRadius: 'var(--radius-default)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              No Xero organisation connected yet.
            </div>
            <button style={buttonPrimary} onClick={handleConnect} disabled={connecting}>
              {connecting ? 'Redirecting to Xero…' : 'Connect Xero'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
            {tenants.map((t) => (
              <div
                key={t.tenantId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--spacing-3)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-default)',
                  backgroundColor: 'var(--color-background)',
                  flexWrap: 'wrap',
                  gap: 'var(--spacing-3)',
                }}
              >
                <div style={{ flex: '1 1 240px', minWidth: 200 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-2)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <strong style={{ fontSize: 'var(--font-size-base)' }}>{t.tenantName}</strong>
                    <span style={statusBadge}>Connected</span>
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      marginTop: 4,
                    }}
                  >
                    {relativeExpiry(t.tokenExpiresAt)}
                    {t.connectedByEmail && ` · by ${t.connectedByEmail}`}
                  </div>
                </div>
                <button
                  style={buttonDanger}
                  onClick={() =>
                    setDisconnectTarget({ tenantId: t.tenantId, tenantName: t.tenantName })
                  }
                >
                  Disconnect
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <PayrollMapping tenants={tenants} onReconsent={handleConnect} />

      <div style={{ ...cardStyle, fontSize: 'var(--font-size-sm)' }}>
        <h3 style={{ marginTop: 0 }}>Where things live</h3>
        <ul style={{ marginTop: 0, marginBottom: 'var(--spacing-3)', paddingLeft: 20 }}>
          <li>
            <strong>Run weekly payroll</strong> →{' '}
            <Link to="/payroll/run" style={{ color: 'var(--color-accent)' }}>
              Payroll · Weekly run
            </Link>{' '}
            — pick a week, see hours/tips/bonus/super, click Pay.
          </li>
          <li>
            <strong>Map barbers to Xero</strong> ↑ on this page (one-time setup, also when you add a
            new barber).
          </li>
          <li>
            <strong>Leave</strong> →{' '}
            <Link to="/payroll/leave" style={{ color: 'var(--color-accent)' }}>
              Payroll · Leave
            </Link>{' '}
            — record leave; bookings on those dates are blocked automatically.
          </li>
        </ul>
        <p
          style={{
            marginTop: 0,
            marginBottom: 0,
            color: 'var(--color-text-muted)',
            fontStyle: 'italic',
          }}
        >
          Tax (PAYG), super clearing, leave accruals, and STP filing happen in Xero when you post
          the pay run there — this app calculates wages, tips, and bonus and pushes the numbers
          across.
        </p>
      </div>

      <ConfirmDialog
        isOpen={Boolean(disconnectTarget)}
        title={`Disconnect ${disconnectTarget?.tenantName ?? ''}?`}
        message="This revokes the connection on Xero's side and removes our stored tokens. You can reconnect at any time. Existing barber mappings for this organisation will be deleted."
        confirmLabel="Disconnect"
        tone="danger"
        onConfirm={confirmDisconnect}
        onCancel={() => setDisconnectTarget(null)}
      />
    </div>
  );
}
